import { gql, useQuery, useApolloClient } from "@apollo/client";
import React, { useState, useContext } from "react";
import {
  LoadingView,
  InvalidReasons,
  NotificationContext,
  ResponsiveTableView,
} from "@solsys/solsys-reactjs";
import ServiceDefinitionDropDown from "../../dropdowns/ServiceDefinitionDropDown";
import { FaTrash, FaPlus } from "react-icons/fa";
import SaveButton from "../elements/SaveButton";
import { useIdempotentCreate, fingerprintProperties, mutationInput } from "../../utils/idempotency";

// GraphQL queries and mutations
const craneInspectionIntervalQuery = gql`
  query craneInspectionIntervals($crane_inspection_interval_list_request: crane_inspection_interval_list_request) {
    crane_inspection_interval(crane_inspection_interval_list_request: $crane_inspection_interval_list_request) {
      edges {
        id
        crane_id
        service_definition_id
        interval_value
        interval_unit
        last_inspection_date
        next_inspection_date
        service_definition {
          id
          name
        }
      }
      totalCount
    }
  }
`;

const craneInspectionIntervalMutation = gql`
  mutation craneInspectionIntervalMutate($crane_inspection_interval_mutation_request: MutationInput) {
    crane_inspection_interval(crane_inspection_interval_mutation_request: $crane_inspection_interval_mutation_request) {
      id
      isNew
    }
  }
`;

const allServiceDefinitionsQuery = gql`
  query allServiceDefinitions($service_definition_list_request: service_definition_list_request) {
    service_definition(service_definition_list_request: $service_definition_list_request) {
      edges {
        id
        name
      }
      totalCount
    }
  }
`;

export default function InspectionIntervals({ craneId, readOnly = false }) {
  const { addNotification } = useContext(NotificationContext);
  const client = useApolloClient();
  const [intervals, setIntervals] = useState([]);
  const [allServiceDefinitions, setAllServiceDefinitions] = useState([]);
  const [invalidReasons, setInvalidReasons] = useState([]);

  const [newServiceDefinitionId, setNewServiceDefinitionId] = useState("");
  const [newIntervalValue, setNewIntervalValue] = useState("");
  const [newIntervalUnit, setNewIntervalUnit] = useState("days");
  const intervalProperties = [
    { key: "crane_id", number: Number(craneId) },
    { key: "service_definition_id", number: Number(newServiceDefinitionId) },
    { key: "interval_value", number: Number(newIntervalValue) },
    { key: "interval_unit", string: newIntervalUnit },
  ];
  const { idempotencyKey, isSaving, trackSave, resetAfterSuccess } = useIdempotentCreate({
    fingerprint: fingerprintProperties(intervalProperties),
  });

  const { loading: loadingIntervals, refetch: refetchIntervals } = useQuery(craneInspectionIntervalQuery, {
    variables: {
      crane_inspection_interval_list_request: {
        filters: [{ field: "crane_id", operator: "=", value: String(craneId) }],
        sorting: [{ field: "service_definition_id", direction: "ASC" }],
      },
    },
    skip: !craneId,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const intervalsList = data?.crane_inspection_interval?.edges ?? [];
      setIntervals(intervalsList);
    },
    onError: (error) => {
      console.error("Error fetching inspection intervals:", error);
      addNotification("bad", "Failed to load inspection intervals");
    },
  });

  const { loading: loadingDefinitions } = useQuery(allServiceDefinitionsQuery, {
    variables: {
      service_definition_list_request: {
        pagination: { limit: 10000 },
        filters: [],
        sorting: [{ field: "name", direction: "ASC" }],
      },
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const definitions = data?.service_definition?.edges ?? [];
      setAllServiceDefinitions(definitions);
    },
    onError: (error) => {
      console.error("Error fetching service definitions:", error);
    },
  });

  const updateInterval = async (intervalId, field, value) => {
    try {
      const interval = intervals.find((i) => i.id === intervalId);
      if (!interval) return;

      const updates = {
        ...interval,
        [field]: value,
      };

      if ((field === "interval_value" || field === "interval_unit") && updates.last_inspection_date) {
        const lastDate = new Date(updates.last_inspection_date);
        const nextDate = calculateNextInspectionDate(lastDate, updates.interval_value, updates.interval_unit);
        updates.next_inspection_date = nextDate.toISOString().split("T")[0];
      }
      if (field === "last_inspection_date" && updates.last_inspection_date) {
        const lastDate = new Date(updates.last_inspection_date);
        const nextDate = calculateNextInspectionDate(lastDate, updates.interval_value, updates.interval_unit);
        updates.next_inspection_date = nextDate.toISOString().split("T")[0];
      }

      await client.mutate({
        mutation: craneInspectionIntervalMutation,
        variables: {
          crane_inspection_interval_mutation_request: {
            id: intervalId,
            properties: [
              { key: "crane_id", number: Number(craneId) },
              { key: "service_definition_id", number: Number(updates.service_definition_id) },
              { key: "interval_value", number: Number(updates.interval_value) },
              { key: "interval_unit", string: updates.interval_unit },
              ...(updates.last_inspection_date ? [{ key: "last_inspection_date", string: updates.last_inspection_date }] : []),
              ...(updates.next_inspection_date ? [{ key: "next_inspection_date", string: updates.next_inspection_date }] : []),
            ],
          },
        },
      });

      const result = await refetchIntervals();
      const freshList = result?.data?.crane_inspection_interval?.edges ?? [];
      setIntervals(freshList);
      addNotification("good", "Inspection interval updated");
    } catch (error) {
      console.error("Error updating interval:", error);
      addNotification("bad", "Failed to update inspection interval");
    }
  };

  const deleteInterval = async (intervalId) => {
    if (!window.confirm("Are you sure you want to delete this inspection interval?")) {
      return;
    }

    try {
      await client.mutate({
        mutation: craneInspectionIntervalMutation,
        variables: {
          crane_inspection_interval_mutation_request: {
            id: intervalId,
            shouldDelete: true,
            properties: [{ key: "crane_id", number: Number(craneId) }],
          },
        },
      });

      const result = await refetchIntervals();
      const freshList = result?.data?.crane_inspection_interval?.edges ?? [];
      setIntervals(freshList);
      addNotification("good", "Inspection interval deleted");
    } catch (error) {
      console.error("Error deleting interval:", error);
      addNotification("bad", "Failed to delete inspection interval");
    }
  };

  const addInterval = async () => {
    const reasons = [];
    if (!newServiceDefinitionId) reasons.push("Service Definition is required.");
    if (!newIntervalValue || isNaN(Number(newIntervalValue)) || Number(newIntervalValue) <= 0) {
      reasons.push("Interval value must be a positive number.");
    }

    const existing = intervals.find((i) => Number(i.service_definition_id) === Number(newServiceDefinitionId));
    if (existing) {
      reasons.push("An inspection interval already exists for this service definition.");
    }

    setInvalidReasons(reasons);
    if (reasons.length > 0) return;

    await trackSave(async () => {
    try {
      const nextDate = calculateNextInspectionDate(new Date(), Number(newIntervalValue), newIntervalUnit);

      await client.mutate({
        mutation: craneInspectionIntervalMutation,
        variables: {
          crane_inspection_interval_mutation_request: mutationInput({
            properties: [
              { key: "crane_id", number: Number(craneId) },
              { key: "service_definition_id", number: Number(newServiceDefinitionId) },
              { key: "interval_value", number: Number(newIntervalValue) },
              { key: "interval_unit", string: newIntervalUnit },
              { key: "next_inspection_date", string: nextDate.toISOString().split("T")[0] },
            ],
            idempotencyKey,
          }),
        },
      });

      const result = await refetchIntervals();
      const freshList = result?.data?.crane_inspection_interval?.edges ?? [];
      setIntervals(freshList);
      setNewServiceDefinitionId("");
      setNewIntervalValue("");
      setNewIntervalUnit("days");
      setInvalidReasons([]);
      resetAfterSuccess();
      addNotification("good", "Inspection interval added");
    } catch (error) {
      console.error("Error adding interval:", error);
      addNotification("bad", "Failed to add inspection interval");
    }
    });
  };

  const calculateNextInspectionDate = (lastDate, value, unit) => {
    const nextDate = new Date(lastDate);

    switch (unit) {
      case "days":
        nextDate.setDate(nextDate.getDate() + value);
        break;
      case "weeks":
        nextDate.setDate(nextDate.getDate() + value * 7);
        break;
      case "months":
        nextDate.setMonth(nextDate.getMonth() + value);
        break;
      case "years":
        nextDate.setFullYear(nextDate.getFullYear() + value);
        break;
      default:
        nextDate.setDate(nextDate.getDate() + value);
    }

    return nextDate;
  };

  const updateLastInspectionDate = async (intervalId, date) => {
    await updateInterval(intervalId, "last_inspection_date", date || "");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getAvailableServiceDefinitions = () => {
    const usedIds = new Set(intervals.map((i) => i.service_definition_id));
    return allServiceDefinitions.filter((def) => !usedIds.has(Number(def.id)));
  };

  if (loadingIntervals || loadingDefinitions) {
    return <LoadingView />;
  }

  return (
    <div style={ { marginTop: 20 } }>
      <h3 style={ { marginBottom: "15px" } }>Inspection Intervals</h3>
      <p style={ { marginBottom: "20px", color: "#6c757d", fontSize: "14px" } }>
        Define inspection intervals for each service definition. The system will track when inspections are due.
      </p>

      { intervals.length === 0 ? (
        <div style={ { padding: 40, textAlign: "center", color: "#6c757d", fontSize: 14 } }>
          No inspection intervals defined. Add one below to get started.
        </div>
      ) : (
        <ResponsiveTableView
          style={ { height: "100%", minHeight: "200px" } }
          loading={ false }
          headers={ [
            { label: "Service Definition" },
            { label: "Interval" },
            { label: "Last Inspection" },
            { label: "Next Inspection" },
            ...(!readOnly ? [{ label: "Actions" }] : []),
          ] }
          data={ intervals.map((interval) => ({
            "Service Definition": interval.service_definition?.name || "Unknown",
            Interval: (
              <div style={ { display: "flex", alignItems: "center" } }>
                <input
                  type="number"
                  min="1"
                  value={ interval.interval_value || "" }
                  onChange={ (e) => updateInterval(interval.id, "interval_value", e.target.value) }
                  disabled={ readOnly }
                  style={ { width: "80px" } }
                />
                <select
                  value={ interval.interval_unit || "days" }
                  onChange={ (e) => updateInterval(interval.id, "interval_unit", e.target.value) }
                  disabled={ readOnly }
                  style={ { marginLeft: 8 } }
                >
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </div>
            ),
            "Last Inspection": (
              <input
                type="date"
                value={ interval.last_inspection_date ? interval.last_inspection_date.split("T")[0] : "" }
                onChange={ (e) => updateLastInspectionDate(interval.id, e.target.value) }
                disabled={ readOnly }
              />
            ),
            "Next Inspection": formatDate(interval.next_inspection_date),
            ...(!readOnly
              ? {
                Actions: (
                  <button
                    style={ { background: "#dc3545", color: "#fff" } }
                    onClick={ () => deleteInterval(interval.id) }
                    title="Delete interval"
                  >
                    <FaTrash /> Delete
                  </button>
                ),
              }
              : {}),
          })) }
          identityColumnKey="Service Definition"
          addMoreResults={ () => {} }
        />
      ) }

      { !readOnly && (
        <>
          <div
            className="edit-section"
            style={ { padding: 15, background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 4, marginTop: 20 } }
          >
            <div className="edit-property" style={ { flex: 1 } }>
              <label>Service Definition:</label>
              <ServiceDefinitionDropDown
                selected={ newServiceDefinitionId }
                onSelect={ setNewServiceDefinitionId }
                searchEnabled
                filters={
                  getAvailableServiceDefinitions().length > 0
                    ? [{ field: "id", operator: "in", value: `(${getAvailableServiceDefinitions().map((d) => d.id).join(", ")})` }]
                    : []
                }
              />
            </div>
            <div className="edit-property" style={ { flex: 1 } }>
              <label>Interval:</label>
              <div style={ { display: "flex", alignItems: "center" } }>
                <input
                  type="number"
                  min="1"
                  value={ newIntervalValue }
                  onChange={ (e) => setNewIntervalValue(e.target.value) }
                  placeholder="Enter value"
                  style={ { width: "100px" } }
                />
                <select
                  value={ newIntervalUnit }
                  onChange={ (e) => setNewIntervalUnit(e.target.value) }
                  style={ { marginLeft: 8 } }
                >
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </div>
            </div>
            <SaveButton
              onClick={ addInterval }
              saving={ isSaving }
              style={ { marginTop: 15, height: "36px", padding: "4px 10px" } }
            >
              <FaPlus /> Add Interval
            </SaveButton>
          </div>

          { invalidReasons.length > 0 && <InvalidReasons reasons={ invalidReasons } /> }
        </>
      ) }
    </div>
  );
}
